import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  FaBrain,
  FaBullseye,
  FaLightbulb,
  FaUsers,
  FaShieldAlt,
  FaPlayCircle,
  FaUserCheck,
  FaThumbsUp,
} from "react-icons/fa";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { setPostAuthRedirect } from "../../utils/postAuthRedirect";
import {
  CHALLENGE_SUBMISSION_DEADLINE,
  CHALLENGE_GRAND_FINALE,
  CHALLENGE_MAX_VIDEO_BYTES,
  CHALLENGE_MAX_VIDEO_SECONDS,
  SOCIAL_PLATFORMS,
  BIO_MIN_WORDS,
  BIO_MAX_WORDS,
} from "../../config/challenge";
import "./ChallengePage.css";

const WHO_CAN_APPLY = [
  "JAMB Tutors",
  "WAEC Tutors",
  "Adult Education Tutors",
  "Trainers and Teachers across Nigeria",
];

const SELECTION_STEPS = [
  {
    icon: FaShieldAlt,
    title: "Profile Verification",
    desc: "TutorsLinks verifies the applicant's profile and eligibility.",
  },
  {
    icon: FaPlayCircle,
    title: "Video Review",
    desc: "Submitted teaching videos are reviewed for compliance, originality and quality.",
  },
  {
    icon: FaUserCheck,
    title: "Expert Selection",
    desc: "Qualified entries are assessed by the competition review/judging team.",
  },
  {
    icon: FaThumbsUp,
    title: "Social Media Voting",
    desc: "Selected presentations are published on the contestant's chosen platform for public engagement/voting.",
  },
];

const QUALITY_PILLS = [
  { icon: FaBrain, label: "Clarity" },
  { icon: FaBullseye, label: "Accuracy" },
  { icon: FaLightbulb, label: "Creativity" },
  { icon: FaUsers, label: "Engagement" },
];

const wordCount = (text) => text.trim().split(/\s+/).filter(Boolean).length;

const getTimeLeft = () => {
  const diff = Math.max(0, new Date(CHALLENGE_GRAND_FINALE).getTime() - Date.now());
  const pad = (n) => String(n).padStart(2, "0");
  return {
    days: pad(Math.floor(diff / 86400000)),
    hours: pad(Math.floor((diff % 86400000) / 3600000)),
    minutes: pad(Math.floor((diff % 3600000) / 60000)),
    seconds: pad(Math.floor((diff % 60000) / 1000)),
  };
};

// Reads a video file's duration client-side before it ever gets uploaded,
// so a too-long entry is rejected instantly instead of after a slow upload.
const getVideoDuration = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    const url = URL.createObjectURL(file);
    video.src = url;
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(video.duration);
    };
    video.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not read video file."));
    };
  });

const emptyForm = {
  presentationTitle: "",
  bio: "",
  platform: "",
  socialProfileUrl: "",
  agreedToTerms: false,
};

function CountdownBlock() {
  const [t, setT] = useState(getTimeLeft());
  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);
  const units = [
    ["Days", t.days],
    ["Hours", t.hours],
    ["Minutes", t.minutes],
    ["Seconds", t.seconds],
  ];
  return (
    <div className="cp-countdown-card">
      <div className="cp-countdown-label">Countdown to the Grand Finale</div>
      <div className="cp-countdown-grid">
        {units.map(([label, value], i) => (
          <div
            className={`cp-countdown-cell${i === units.length - 1 ? " cp-countdown-cell-accent" : ""}`}
            key={label}
          >
            <div className="cp-countdown-value">{value}</div>
            <div className="cp-countdown-unit">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChallengePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [entry, setEntry] = useState(null);
  const [loadingEntry, setLoadingEntry] = useState(false);

  // "form" → filling in details, "confirm" → review-before-submit screen
  const [stage, setStage] = useState("form");
  const [form, setForm] = useState(emptyForm);
  const [videoFile, setVideoFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  const isTutor = user?.role === "tutor";
  const isProfileComplete = user?.profileCompleted === true;
  const deadlinePassed = new Date() > new Date(CHALLENGE_SUBMISSION_DEADLINE);
  const bioWords = wordCount(form.bio);

  useEffect(() => {
    if (!isTutor) return;
    setLoadingEntry(true);
    api
      .get("/challenge/entries/me")
      .then((res) => setEntry(res.data?.entry || null))
      .catch(() => setEntry(null))
      .finally(() => setLoadingEntry(false));
  }, [isTutor]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    setError("");
    setVideoFile(null);
    if (!file) return;

    if (!["video/mp4", "video/quicktime"].includes(file.type)) {
      setError("Please upload an MP4 or MOV video.");
      return;
    }
    if (file.size > CHALLENGE_MAX_VIDEO_BYTES) {
      setError("Video must be 10MB or smaller.");
      return;
    }

    try {
      const duration = await getVideoDuration(file);
      if (duration > CHALLENGE_MAX_VIDEO_SECONDS) {
        setError("Video must be 3 minutes or shorter.");
        return;
      }
    } catch {
      setError("Could not read that video file. Please try another.");
      return;
    }

    setVideoFile(file);
  };

  const validateForm = () => {
    if (!form.presentationTitle.trim()) return "Please enter a presentation title.";
    if (!videoFile) return "Please select a video to upload.";
    if (bioWords < BIO_MIN_WORDS || bioWords > BIO_MAX_WORDS)
      return `Your biography must be between ${BIO_MIN_WORDS} and ${BIO_MAX_WORDS} words (currently ${bioWords}).`;
    if (!form.platform) return "Please select your preferred social media platform.";
    if (!form.socialProfileUrl.trim())
      return "Please enter your social media profile URL or handle.";
    if (!form.agreedToTerms) return "Please accept the Terms and Conditions to continue.";
    return "";
  };

  const handleReview = (e) => {
    e.preventDefault();
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError("");
    setStage("confirm");
  };

  const handleConfirmSubmit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const formData = new FormData();
      formData.append("presentationTitle", form.presentationTitle.trim());
      formData.append("bio", form.bio.trim());
      formData.append("preferredSocialPlatform", form.platform);
      formData.append("socialProfileUrl", form.socialProfileUrl.trim());
      formData.append("agreedToTerms", "true");
      formData.append("video", videoFile);

      const res = await api.post("/challenge/entries", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setEntry(res.data.entry);
      setForm(emptyForm);
      setVideoFile(null);
      setStage("form");
      toast.success("Entry submitted!");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to submit entry.";
      setError(msg);
      toast.error(msg);
      setStage("form");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApplyClick = () => {
    if (!user) {
      setPostAuthRedirect("/award");
      navigate("/login");
      return;
    }
    if (isTutor && !isProfileComplete) {
      setPostAuthRedirect("/award");
      navigate("/tutor/registration");
    }
  };

  const profileField = (label, value) => (
    <div className="cp-profile-field" key={label}>
      <span>{label}</span>
      <strong>{value?.trim() ? value : "Not provided — update in your profile"}</strong>
    </div>
  );

  const renderApplySection = () => {
    if (entry) {
      return (
        <div className="cp-success-box">
          <div className="cp-success-icon">🏆</div>
          <h3>Entry Successfully Submitted</h3>
          <p>
            Your submission is now under review. You will be contacted if your
            entry progresses to the next stage.
          </p>
        </div>
      );
    }

    if (deadlinePassed) {
      return (
        <div className="cp-status-box cp-status-closed">
          Submissions closed on 31st October 2026.
        </div>
      );
    }

    if (!user) {
      return (
        <button className="cp-btn cp-btn-primary" onClick={handleApplyClick}>
          Login / Signup to Apply
        </button>
      );
    }

    if (!isTutor) {
      return (
        <div className="cp-status-box">This challenge is open to tutors only.</div>
      );
    }

    if (!isProfileComplete) {
      return (
        <button className="cp-btn cp-btn-primary" onClick={handleApplyClick}>
          Complete Your Profile to Apply
        </button>
      );
    }

    if (loadingEntry) {
      return <div className="cp-status-box">Loading…</div>;
    }

    if (stage === "confirm") {
      return (
        <div className="cp-form cp-confirm">
          <h3>Confirm Your Entry</h3>
          <div className="cp-confirm-row">
            <span>Presentation Title</span>
            <strong>{form.presentationTitle}</strong>
          </div>
          <div className="cp-confirm-row">
            <span>Social Media Platform</span>
            <strong>{form.platform}</strong>
          </div>
          <div className="cp-confirm-row">
            <span>Social Media Handle</span>
            <strong>{form.socialProfileUrl}</strong>
          </div>
          <div className="cp-confirm-row cp-confirm-bio">
            <span>Biography</span>
            <p>{form.bio}</p>
          </div>
          <div className="cp-confirm-row">
            <span>Video File</span>
            <strong>{videoFile?.name}</strong>
          </div>

          {error && <p className="cp-form-error">{error}</p>}

          <div className="cp-form-actions">
            <button
              type="button"
              className="cp-btn cp-btn-outline-dark"
              onClick={() => setStage("form")}
              disabled={submitting}
            >
              Back to Edit
            </button>
            <button
              type="button"
              className="cp-btn cp-btn-primary"
              onClick={handleConfirmSubmit}
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Confirm & Submit Entry"}
            </button>
          </div>
        </div>
      );
    }

    return (
      <form className="cp-form" onSubmit={handleReview}>
        <div className="cp-profile-summary">
          <div className="cp-profile-summary-title">Your TutorsLinks Profile</div>
          <div className="cp-profile-grid">
            {profileField(
              "Full Name",
              `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
            )}
            {profileField("Email", user?.email)}
            {profileField("Phone", user?.phone)}
            {profileField("School/Training Centre", user?.schoolName)}
            {profileField("State of Residence", user?.state)}
          </div>
        </div>

        <p className="cp-form-note">
          Only <strong>one entry is permitted per applicant</strong>. Please
          review your details carefully before submitting.
        </p>

        <div className="cp-form-group">
          <label>Presentation Title</label>
          <input
            type="text"
            placeholder="e.g. Mastering Quadratic Equations in 3 Minutes"
            value={form.presentationTitle}
            onChange={(e) => setField("presentationTitle", e.target.value)}
          />
        </div>

        <div className="cp-form-group">
          <label>Upload your entry video (MP4/MOV, max 10MB, max 3 minutes)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/quicktime"
            onChange={handleFileChange}
          />
          {videoFile && (
            <p className="cp-file-selected">
              Selected: {videoFile.name} ({(videoFile.size / (1024 * 1024)).toFixed(1)}MB)
            </p>
          )}
        </div>

        <div className="cp-form-group">
          <label>
            Applicant Biography{" "}
            <span
              className={`cp-word-counter${
                form.bio && (bioWords < BIO_MIN_WORDS || bioWords > BIO_MAX_WORDS)
                  ? " cp-word-counter-error"
                  : ""
              }`}
            >
              {bioWords} / {BIO_MIN_WORDS}-{BIO_MAX_WORDS} words
            </span>
          </label>
          <textarea
            rows={5}
            placeholder="Tell us briefly about yourself, your teaching experience, subject area and educational impact."
            value={form.bio}
            onChange={(e) => setField("bio", e.target.value)}
          />
        </div>

        <div className="cp-form-group">
          <label>Preferred Social Media Platform</label>
          <select value={form.platform} onChange={(e) => setField("platform", e.target.value)}>
            <option value="">— Select a platform —</option>
            {SOCIAL_PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <div className="cp-form-group">
          <label>Social Media Profile/Handle</label>
          <input
            type="text"
            placeholder="e.g. facebook.com/tutorslinks"
            value={form.socialProfileUrl}
            onChange={(e) => setField("socialProfileUrl", e.target.value)}
          />
          <p className="cp-form-hint">
            We'll post your video here for public voting (likes &amp; comments)
            and share you the link to spread to friends and family.
          </p>
        </div>

        <label className="cp-checkbox-row">
          <input
            type="checkbox"
            checked={form.agreedToTerms}
            onChange={(e) => setField("agreedToTerms", e.target.checked)}
          />
          <span>
            I confirm that this submission is my original work, the
            information provided is accurate, I have submitted only one
            entry, and I authorise TutorsLinks to publish and use my
            submitted video for competition and promotional purposes.
          </span>
        </label>

        {error && <p className="cp-form-error">{error}</p>}

        <div className="cp-form-actions">
          <button type="submit" className="cp-btn cp-btn-primary">
            Review Entry →
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <Header
        isMenuOpen={isMenuOpen}
        onMenuToggle={() => setIsMenuOpen(!isMenuOpen)}
      />
      <div className="cp-page">
      {/* ── Hero ── */}
      <section className="cp-hero">
        <div className="cp-hero-inner">
          <div className="cp-hero-text">
            <div className="cp-badges">
              <span className="cp-badge cp-badge-accent">Nationwide Online Competition</span>
              <span className="cp-badge">Nigeria · 2026</span>
            </div>

            <div className="cp-eyebrow-light">TutorsLinks</div>
            <h1 className="cp-hero-title">
              Educator Impact
              <br />
              Challenge <span>2026</span>
            </h1>
            <div className="cp-hero-tagline">Teach. Impact. Inspire.</div>
            <p className="cp-hero-subtitle">
              A nationwide online competition for Tutors, Teachers and
              Trainers across Nigeria
            </p>

            <CountdownBlock />

            <div className="cp-prize-row">
              <div className="cp-prize-card">
                <div className="cp-prize-value">₦1,000,000</div>
                <div className="cp-prize-caption">Total Cash Prizes</div>
              </div>
              <div className="cp-prize-card">
                <div className="cp-prize-value">10 Winners</div>
                <div className="cp-prize-caption">₦100,000 Each</div>
              </div>
            </div>

            <div className="cp-hero-ctas">
              <a href="#apply" className="cp-btn cp-btn-primary">
                Login / Signup to Apply
              </a>
              <a href="#challenge" className="cp-btn cp-btn-outline-light">
                See the challenge
              </a>
            </div>
          </div>

          <div className="cp-hero-image">
            <img
              src="/images/educator-impact.jpg"
              alt="TutorsLinks Educator Impact Challenge 2026"
              onError={(e) => (e.target.style.display = "none")}
            />
          </div>
        </div>
      </section>

      {/* ── The Challenge ── */}
      <section id="challenge" className="cp-section cp-section-light">
        <div className="cp-section-inner">
          <div className="cp-col2">
            <div>
              <div className="cp-eyebrow">The Challenge</div>
              <h2 className="cp-heading">Teach Us Something Useful in 3 Minutes</h2>
            </div>
            <div className="cp-challenge-copy">
              <p>
                Submit an original teaching video demonstrating your ability
                to explain a useful concept clearly, accurately and
                engagingly. Your presentation may relate to JAMB, WAEC, Adult
                Education or another area in which you actively tutor.
                Entries should demonstrate teaching quality, subject
                knowledge, clarity, creativity and learner engagement.
              </p>
              <div className="cp-pill-row">
                {QUALITY_PILLS.map((q) => (
                  <span className="cp-pill" key={q.label}>
                    <q.icon size={13} /> {q.label}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="cp-photo-strip">
            <div className="cp-photo-strip-track">
              <img src="/images/teaching-kid-write.jpg" alt="Tutor teaching a student" />
              <img src="/images/study-group1.jpg" alt="Students in a study group" />
              <img src="/images/tutoring.jpg" alt="Tutoring session" />
              <img
                src="/images/teaching-kid-write.jpg"
                alt=""
                aria-hidden="true"
                className="cp-photo-dup"
              />
              <img
                src="/images/study-group1.jpg"
                alt=""
                aria-hidden="true"
                className="cp-photo-dup"
              />
              <img
                src="/images/tutoring.jpg"
                alt=""
                aria-hidden="true"
                className="cp-photo-dup"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Eligibility ── */}
      <section className="cp-section cp-section-alt">
        <div className="cp-section-inner cp-col2">
          <div>
            <div className="cp-eyebrow">Eligibility</div>
            <h2 className="cp-heading">Who Can Apply?</h2>
          </div>
          <div className="cp-eligibility-grid">
            {WHO_CAN_APPLY.map((role) => (
              <div className="cp-eligibility-card" key={role}>
                {role}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Key Dates ── */}
      <section className="cp-section cp-section-light">
        <div className="cp-section-inner">
          <div className="cp-eyebrow cp-center-mobile">Key Dates</div>
          <div className="cp-dates-grid">
            <div className="cp-date-card">
              <div className="cp-date-label">Submissions close</div>
              <div className="cp-date-value">31st October 2026</div>
            </div>
            <div className="cp-date-card cp-date-card-dark">
              <div className="cp-date-label">Grand Finale</div>
              <div className="cp-date-value cp-date-value-accent">28th November 2026</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Selection Format ── */}
      <section className="cp-section cp-section-alt">
        <div className="cp-section-inner">
          <div className="cp-eyebrow cp-center-mobile">Selection Format</div>
          <div className="cp-steps-grid">
            {SELECTION_STEPS.map((step, i) => (
              <div className="cp-step-card" key={step.title}>
                <span className="cp-step-badge">Step {i + 1}</span>
                <div className="cp-step-icon">
                  <step.icon size={17} />
                </div>
                <strong>{step.title}</strong>
                <p>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Apply ── */}
      <section id="apply" className="cp-section cp-section-light">
        <div className="cp-section-inner cp-apply-inner">
          <div className="cp-eyebrow cp-center-mobile">Apply Now</div>
          {renderApplySection()}
        </div>
      </section>

      </div>
      <Footer />
    </>
  );
}
